/* sc-cart-remove-confirm.js
 * LIVE parity confirm dialog for cart item removal.
 * Intercepts:
 *   - <cart-remove-button> clicks (trash icon)
 *   - quantity minus button when current qty === 1
 * Shows modal with item image + "Keep in cart" / "Yes, remove" actions.
 * On confirm: delegates to the row remove control with guard flag bypass.
 */
(function () {
  'use strict';

  var MODAL_ID = 'sc-cart-remove-modal';
  var BYPASS_FLAG = '__sc_remove_confirmed';
  var MAGSAFE_HANDLE = 'make-it-magsafe';
  var pendingTarget = null;
  // Captured at open time: the row's line key + surface. The modal can stay
  // open across drawer/page re-renders, after which pendingTarget is a
  // DETACHED node — these let confirmRemove find the equivalent live row.
  var pendingRowKey = '';
  var pendingFromDrawer = false;
  var magsafePrice = 0;
  var magsafeCompareAtPrice = 0;
  var magsafePricePromise = null;

  function ensureMagSafePrice() {
    if (magsafePricePromise) return magsafePricePromise;
    magsafePricePromise = fetch('/products/' + MAGSAFE_HANDLE + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var v = p && p.variants && p.variants[0];
        if (v && v.price) magsafePrice = v.price;
        if (v && v.compare_at_price) magsafeCompareAtPrice = v.compare_at_price;
        return magsafeCompareAtPrice;
      })
      .catch(function () { return 0; });
    return magsafePricePromise;
  }

  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  function findCartRow(el) {
    // Nested V4 protection row lives INSIDE its parent item — it must win
    // over the parent selectors so the modal describes the child, not the parent.
    return el.closest('.sc-cart-protection, tr.cart-item, .cart-drawer-item, .cart-drawer__item, cart-drawer-items > *');
  }

  function getRemovalTarget(target) {
    if (!target || !target.closest) return target;
    var minusBtn = target.closest('.quantity__button[name="minus"], button[name="minus"]');
    if (minusBtn !== target) return target;
    var row = findCartRow(target);
    return (row && row.querySelector('cart-remove-button')) || target;
  }

  function getItemImage(row) {
    if (!row) return '';
    var img = row.querySelector('.sc-preview-show img, .cart-item__image, img.cart-drawer-item__image, img');
    return img ? img.getAttribute('src') || img.src : '';
  }

  function isShippingProtection(row) {
    if (!row) return false;
    // V4 nested protection row (compact checkbox under the parent product)
    if (row.classList && row.classList.contains('sc-cart-protection')) return true;
    // Vendor anchor still in DOM (display:none) with product handle in href.
    var link = row.querySelector('a[href*="shipping-insurance"], a[href*="shipping-protection"]');
    if (link) return true;
    // Fallback: visible product name
    var nameEl = row.querySelector('.cart-item__name, .cart-drawer-item__name');
    if (nameEl && /shipping\s*(protection|insurance)/i.test(nameEl.textContent)) return true;
    return false;
  }

  function isMagSafe(row) {
    return !!(row && row.classList
      && row.classList.contains('cart-item--product-' + MAGSAFE_HANDLE));
  }

  function isTough(row) {
    return !!(row && row.classList
      && row.classList.contains('cart-item--product-make-it-tough'));
  }

  function detectVariant(row) {
    if (isMagSafe(row)) return 'magsafe';
    if (isTough(row)) return 'tough';
    if (isShippingProtection(row)) return 'sp';
    return 'default';
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    var fmt = (window.Shopify && Shopify.formatMoney) ? Shopify.formatMoney : null;
    if (fmt) try { return fmt(cents, '${{amount}}'); } catch (e) {}
    return '$' + (cents / 100).toFixed(2);
  }

  function readRowPriceFromDom(row) {
    if (!row) return { price: '', compare: '' };
    var compareEl = row.querySelector('.sc-cart-price__compare') || row.querySelector('.cart-item__old-price');
    var currentEl = row.querySelector('.sc-cart-price__current') || row.querySelector('.regular-price');
    var previewTrigger = row.querySelector('.sc-preview-show[data-price]');
    var priceStr = (currentEl && currentEl.textContent.trim()) ||
      (previewTrigger && previewTrigger.getAttribute('data-price')) || '';
    var compareStr = (compareEl && compareEl.textContent.trim()) ||
      (previewTrigger && previewTrigger.getAttribute('data-compare-price')) || '';
    return { price: priceStr, compare: compareStr };
  }

  function parseMoneyToCents(value) {
    if (typeof value !== 'string') return 0;
    var normalized = value.replace(/[^0-9.,-]/g, '');
    if (!normalized) return 0;
    var negative = normalized.charAt(0) === '-';
    normalized = normalized.replace(/-/g, '');
    var dotIndex = normalized.lastIndexOf('.');
    var commaIndex = normalized.lastIndexOf(',');
    var separatorIndex = Math.max(dotIndex, commaIndex);
    var decimalIndex = -1;

    if (dotIndex >= 0 && commaIndex >= 0) {
      decimalIndex = separatorIndex;
    } else if (separatorIndex >= 0 && normalized.length - separatorIndex - 1 === 2) {
      decimalIndex = separatorIndex;
    }

    var cents;
    if (decimalIndex >= 0) {
      var whole = normalized.slice(0, decimalIndex).replace(/[.,]/g, '') || '0';
      var fraction = (normalized.slice(decimalIndex + 1).replace(/[.,]/g, '') + '00').slice(0, 2);
      cents = parseInt(whole, 10) * 100 + parseInt(fraction, 10);
    } else {
      cents = parseInt(normalized.replace(/[.,]/g, ''), 10) * 100;
    }
    if (isNaN(cents)) return 0;
    return negative ? -cents : cents;
  }

  function fillMagSafePrice(modal, row, allowDelayedRefresh) {
    var priceWrap = modal.querySelector('.sc-cart-remove-modal__price');
    var compareEl = modal.querySelector('.sc-cart-remove-modal__price-compare');
    var saleEl = modal.querySelector('.sc-cart-remove-modal__price-sale');
    var badgeEl = modal.querySelector('.sc-cart-remove-modal__price-badge');
    if (!priceWrap) return;
    var displayPrice = readRowPriceFromDom(row);
    var price = parseMoneyToCents(displayPrice.price) || magsafePrice;
    if (!price) { priceWrap.setAttribute('aria-hidden', 'true'); return; }
    var compare = parseMoneyToCents(displayPrice.compare) ||
      magsafeCompareAtPrice;
    if (saleEl) saleEl.textContent = displayPrice.price || formatMoney(price);
    if (compare && compare > price) {
      if (compareEl) { compareEl.textContent = displayPrice.compare || formatMoney(compare); compareEl.hidden = false; }
      if (badgeEl) {
        var pct = Math.round((1 - price / compare) * 100);
        badgeEl.textContent = 'SAVE ' + pct + '%';
        badgeEl.hidden = false;
      }
    } else {
      if (compareEl) compareEl.hidden = true;
      if (badgeEl) badgeEl.hidden = true;

      // The conditional product request can still be in flight when the
      // shopper opens the modal. Refresh this same modal once the compare-at
      // price arrives, without reopening it or changing the pending action.
      if (allowDelayedRefresh !== false && magsafePricePromise) {
        ensureMagSafePrice().then(function (compareAtPrice) {
          if (!compareAtPrice || !modal.classList.contains('is-open')) return;
          if (modal.getAttribute('data-state') !== 'magsafe') return;
          if (!pendingTarget || findCartRow(pendingTarget) !== row) return;
          fillMagSafePrice(modal, row, false);
        });
      }
    }
    priceWrap.setAttribute('aria-hidden', 'false');
  }

  function applyVariant(modal, variant, row) {
    var titleEl = modal.querySelector('.sc-cart-remove-modal__title');
    var subtextEl = modal.querySelector('.sc-cart-remove-modal__subtext');
    var keepBtn = modal.querySelector('.sc-cart-remove-modal__btn--keep');
    var removeBtn = modal.querySelector('.sc-cart-remove-modal__btn--remove');
    var priceWrap = modal.querySelector('.sc-cart-remove-modal__price');
    modal.setAttribute('data-state', variant);
    if (priceWrap) priceWrap.setAttribute('aria-hidden', 'true');
    if (variant === 'magsafe') {
      if (titleEl) titleEl.textContent = 'Skip wireless charging?';
      if (subtextEl) subtextEl.textContent = "Your case won't snap onto MagSafe chargers or accessories.";
      if (keepBtn) keepBtn.textContent = 'Keep MagSafe';
      if (removeBtn) removeBtn.textContent = 'No thanks, remove';
      /* Price data is only needed for this modal variant. Avoid the former
       * eager /cart.js request on every PDP/cart visit. */
      ensureMagSafePrice();
      fillMagSafePrice(modal, row);
    } else if (variant === 'tough') {
      if (titleEl) titleEl.textContent = 'Remove the Tough Case upgrade?';
      if (subtextEl) subtextEl.textContent = "Your case goes back to the standard shell without the 2-layer drop protection.";
      if (keepBtn) keepBtn.textContent = 'Keep the upgrade';
      if (removeBtn) removeBtn.textContent = 'No thanks, remove it';
    } else if (variant === 'sp') {
      if (titleEl) titleEl.textContent = 'Remove shipping protection?';
      if (subtextEl) subtextEl.textContent = "Your order won't be covered against damage, loss, or theft during shipping.";
      if (keepBtn) keepBtn.textContent = 'Keep the protection';
      if (removeBtn) removeBtn.textContent = 'No thanks, remove it';
    } else {
      if (titleEl) titleEl.textContent = 'Remove this from your cart?';
      if (subtextEl) subtextEl.textContent = 'Your custom design will be lost.';
      if (keepBtn) keepBtn.textContent = 'Keep in cart';
      if (removeBtn) removeBtn.textContent = 'Yes, remove';
    }
  }

  function openModal(target) {
    var modal = getModal();
    if (!modal) return;
    pendingTarget = target;
    var row = findCartRow(target);
    pendingRowKey = (row && row.getAttribute && row.getAttribute('data-cart-item-key')) || '';
    pendingFromDrawer = !!(target.closest && target.closest('cart-drawer'));
    var variant = detectVariant(row);
    applyVariant(modal, variant, row);
    var imgEl = modal.querySelector('.sc-cart-remove-modal__image-item');
    if (imgEl) {
      var imgSrc = getItemImage(row) || '';
      imgEl.src = imgSrc;
      // The V4 nested protection row has no image — hide the empty frame.
      imgEl.hidden = !imgSrc;
    }
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');
    document.body.classList.add('sc-cart-remove-modal-open');
  }

  function closeModal() {
    var modal = getModal();
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove('is-open');
    document.body.classList.remove('sc-cart-remove-modal-open');
    pendingTarget = null;
    pendingRowKey = '';
    pendingFromDrawer = false;
  }

  // The vendor re-render after a removal replaces the drawer contents, so the
  // independently-scrolling body comes back at the top — a shopper deleting an
  // item deep in a long cart lands back at the first item. Capture the
  // scroller offset before triggering the removal and re-apply it on the DOM
  // swaps that follow (short window; sets are idempotent and clamp when the
  // shorter list can't reach the old offset).
  function findDrawerScroller() {
    var candidates = document.querySelectorAll('cart-drawer .cart-drawer__body, cart-drawer .drawer__contents, cart-drawer .drawer__inner');
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].scrollHeight > candidates[i].clientHeight + 1) return candidates[i];
    }
    return null;
  }

  function preserveDrawerScroll() {
    try {
      if (typeof MutationObserver === 'undefined') return;
      var scroller = findDrawerScroller();
      if (!scroller || !scroller.scrollTop) return;
      var top = scroller.scrollTop;
      var inner = document.querySelector('cart-drawer .drawer__inner');
      if (!inner) return;
      var obs = new MutationObserver(function () {
        var ns = findDrawerScroller();
        // Row order is pinned by sc-cart-drawer-row-order's own observer in
        // the same mutation batch (installed earlier → runs first), so this
        // restore lands against the final layout. Stop after the FIRST real
        // restore: the expire-timer countdown mutates the same subtree every
        // second, and staying connected for the whole window would keep
        // yanking the scroller against the shopper's own scrolling. A
        // mutation that left scrollTop intact (e.g. a countdown tick before
        // the swap lands) does NOT disconnect — the reset is still coming.
        if (ns && ns.scrollTop < top) {
          ns.scrollTop = top;
          obs.disconnect();
        }
      });
      obs.observe(inner, { childList: true, subtree: true });
      setTimeout(function () { obs.disconnect(); }, 3000);
    } catch (e) { /* scroll restore is best-effort — never block the removal */ }
  }

  function confirmRemove() {
    if (!pendingTarget) {
      closeModal();
      return;
    }
    var target = getRemovalTarget(pendingTarget);
    var rowKey = pendingRowKey;
    var fromDrawer = pendingFromDrawer;
    closeModal();
    if (fromDrawer) preserveDrawerScroll();
    // A V4 protection toggle can be re-rendered (section swap) while the
    // modal is open — clicking the detached node would silently do nothing.
    // Re-resolve the live button by its identity attributes.
    if (target && target.hasAttribute && target.hasAttribute('data-sc-protection-remove') && target.isConnected === false) {
      var scKey = target.getAttribute('data-sc-protection-key') || '';
      var scPtr = target.getAttribute('data-sc-protection-parent-pointer') || '';
      var cssEsc = window.CSS && CSS.escape ? CSS.escape : function (s) { return s; };
      target = document.querySelector('[data-sc-protection-remove][data-sc-protection-key="' + cssEsc(scKey) + '"][data-sc-protection-parent-pointer="' + cssEsc(scPtr) + '"]');
      if (!target) return; // row is gone — nothing left to remove
    }
    // Same staleness for the vendor trash / minus-at-one target — but here a
    // detached click is NOT harmless: CartRemoveButton binds its listener in
    // the CONSTRUCTOR, so the detached node still POSTs /cart/change with the
    // data-index captured at the previous render. Against the re-rendered
    // cart that index can name a DIFFERENT line (reproduced on LIVE
    // 2026-07-17: removed the wrong personalized item). Re-resolve through
    // the row's line key on the surface the click came from.
    if (target && target.isConnected === false) {
      if (!rowKey) return; // no identity to re-resolve — safer to do nothing
      var esc = window.CSS && CSS.escape ? CSS.escape(rowKey) : rowKey;
      var drawerSel = '.cart-drawer-item[data-cart-item-key="' + esc + '"]';
      var pageSel = 'tr.cart-item[data-cart-item-key="' + esc + '"]';
      var liveRow = fromDrawer
        ? (document.querySelector(drawerSel) || document.querySelector(pageSel))
        : (document.querySelector(pageSel) || document.querySelector(drawerSel));
      target = liveRow ? liveRow.querySelector('cart-remove-button') : null;
      if (!target) return; // row is gone — nothing left to remove
    }
    // The vendor remove path (cart-remove-button → updateQuantity(line, 0))
    // never calls enableLoading — only quantity CHANGES show the row spinner.
    // With a single item in the drawer that leaves zero feedback until the
    // re-render lands, so surface the row's own (already-rendered) spinner
    // and dim the row. The row is replaced by the re-render either way, so
    // this transient state can't leak.
    try {
      var removingRow = findCartRow(target);
      if (removingRow && removingRow.classList) {
        removingRow.classList.add('sc-cart-removing');
        // Both the .loading-overlay wrapper AND its inner spinner carry
        // `hidden` in the server markup — clearing only one keeps it invisible.
        var rowOverlay = removingRow.querySelector('.loading-overlay');
        if (rowOverlay) rowOverlay.classList.remove('hidden');
        var rowSpinner = removingRow.querySelector('.loading-overlay__spinner');
        if (rowSpinner) rowSpinner.classList.remove('hidden');
      }
    } catch (e) { /* feedback only — never block the removal */ }
    target[BYPASS_FLAG] = true;
    // Delegate minus-at-one to the row's native remove control. Replaying the
    // minus click cannot remove because Shrine quantity inputs have min="1".
    target.click();
    // Clear flag after event loop so next click re-triggers modal.
    setTimeout(function () {
      try { delete target[BYPASS_FLAG]; } catch (e) { target[BYPASS_FLAG] = false; }
    }, 100);
  }

  // Capture-phase handler: intercept BEFORE vendor cart-remove-button handler.
  document.addEventListener('click', function (e) {
    var el = e.target;
    if (!el) return;

    // V4 protection checkbox (checked state) — removal runs key-based in
    // sc-cart-protection-toggle.js; this modal only fronts the confirmation.
    var protectionRemove = el.closest && el.closest('[data-sc-protection-remove]');
    if (protectionRemove && !protectionRemove[BYPASS_FLAG]) {
      e.preventDefault();
      e.stopImmediatePropagation();
      openModal(protectionRemove);
      return;
    }

    // Trash icon click (a or button inside cart-remove-button custom element)
    var removeBtn = el.closest('cart-remove-button');
    if (removeBtn && !removeBtn[BYPASS_FLAG]) {
      // Only intercept if the inner clickable was clicked
      var inner = el.closest('a, button');
      if (inner && removeBtn.contains(inner)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        openModal(removeBtn);
        return;
      }
    }

    // Quantity minus when qty === 1 → acts as "remove"
    var minusBtn = el.closest('.quantity__button[name="minus"], button[name="minus"]');
    if (minusBtn && !minusBtn[BYPASS_FLAG]) {
      var qtyWrapper = minusBtn.closest('quantity-input, .quantity');
      var input = qtyWrapper ? qtyWrapper.querySelector('input[type="number"], input.quantity__input') : null;
      if (input && parseInt(input.value, 10) === 1) {
        e.preventDefault();
        e.stopImmediatePropagation();
        openModal(minusBtn);
        return;
      }
    }
  }, true);

  // Modal close / confirm delegation
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target.closest('#' + MODAL_ID)) return;

    if (target.closest('[data-close]')) {
      closeModal();
      return;
    }

    if (target.closest('[data-confirm]')) {
      confirmRemove();
      return;
    }
  });

  // Esc key closes modal
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      var modal = getModal();
      if (modal && modal.classList.contains('is-open')) {
        closeModal();
      }
    }
  });
})();
